import { useEffect, useState } from 'react';
import { useStoreState } from 'pullstate';
import { AppStore } from '../../stores/AppStore';
import { LMSStore } from '../../stores/LMSStore';
import { Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import Header from '../../components/MUI/Header';
import { AddCircleOutline, Delete, Save } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';

import './Courses.css';
import {
    addDocToSubCollection,
    deleteDocFromSubCollection,
    editDocInSubCollection,
} from '../../utils/getData';
import { LoadingButton } from '@mui/lab';

const Courses = () => {
    const { user } = useStoreState(AppStore);
    const { courses } = useStoreState(LMSStore);
    const [addOpen, setAddOpen] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [loading, setLoading] = useState(false);
    const [editDocId, setEditDocId] = useState(null); // keep track of the document being edited

    const options = {
        selectableRows: 'none',
        elevation: 0,
    };
    const columns = [
        {
            name: 'identifier',
            label: 'Identifier',
            options: {
                filter: true,
                sort: true,
                setCellProps: () => ({ style: { minWidth: '200px', maxWidth: '200px' } }),
            },
        },
        {
            name: 'id',
            label: ' ',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    return (
                        <button
                            className='dropdown-edit-button'
                            onClick={() => {
                                const rowData = courses.find((course) => course.id === value);
                                setIdentifier(rowData.identifier);
                                setEditDocId(value); // set the id of the document being edited
                                setAddOpen(true);
                            }}
                        >
                            Edit
                        </button>
                    );
                },
            },
        },
        {
            name: 'id',
            label: ' ',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => {
                    return (
                        <IconButton
                            aria-label='Delete'
                            onClick={() => {
                                if (
                                    window.confirm('Are you sure you want to delete this course?')
                                ) {
                                    deleteDocFromSubCollection(
                                        user.uid,
                                        'courses',
                                        value,
                                        LMSStore
                                    );
                                }
                            }}
                            color='error'
                        >
                            <Delete />
                        </IconButton>
                    );
                },
            },
        },
    ];
    useEffect(() => {
        window.dataLayer.push({
            event: 'pageview',
            page: {
                path: '/courses',
                title: 'Courses',
            },
        });
    }, [user]);

    const addDoc = () => {
        setLoading(true);
        if (editDocId) {
            // Edit existing document
            editDocInSubCollection(user.uid, 'courses', editDocId, { identifier }, LMSStore)
                .then(() => {
                    setLoading(false);
                    setAddOpen(false);
                    setIdentifier('');
                    setEditDocId(null); // reset after editing
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        } else {
            // Add new document
            addDocToSubCollection(user.uid, 'courses', { identifier }, LMSStore)
                .then(() => {
                    setLoading(false);
                    setAddOpen(false);
                    setIdentifier('');
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Grid
                container
                spacing={2}
                className='scanHeader'
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 5,
                    width: '100%',
                }}
            >
                <Grid container item xs={12} md={6}>
                    <Grid item xs={12} md={6}>
                        <Header text={'Courses'} />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                    >
                        {addOpen && (
                            <small className='helper-text no-indent'>
                                Enter an identifier for the course. <br />
                                This can be a course code, course name, or any other identifier you
                                want to use.
                            </small>
                        )}
                    </Grid>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                    gap={2}
                >
                    <Grid item xs={12} md={7}>
                        {addOpen && (
                            <>
                                <TextField
                                    value={identifier}
                                    placeholder='Course Identifier'
                                    sx={{ width: '100%' }}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                />
                            </>
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <LoadingButton
                            loading={loading}
                            variant='contained'
                            onClick={() => (addOpen ? addDoc() : setAddOpen(!addOpen))}
                            color='secondary'
                            startIcon={addOpen ? <Save /> : <AddCircleOutline />}
                            sx={{ width: !addOpen ? '100%' : 'auto' }}
                            disabled={loading || (addOpen && !identifier)}
                            loadingPosition='start'
                        >
                            {addOpen ? 'Save' : loading ? 'Saving' : 'Add Course'}
                        </LoadingButton>
                        {addOpen && (
                            <Button
                                variant='outlined'
                                color='danger'
                                onClick={() => {
                                    setIdentifier('');
                                    setAddOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ marginBottom: 5 }}>
                <Grid item xs={12}>
                    <Typography variant='h6' sx={{ marginBottom: 2 }}>
                        Add a Course
                    </Typography>
                    <p>
                        Add courses using any unique identifier, such as a course name or code. Once
                        you've entered your chosen identifier, hit "Add" and it's ready for future
                        scans. Changes are simple - locate the course on your list and click "Edit"
                        or "Delete".
                    </p>
                </Grid>
                <Grid item xs={12}>
                    {courses && <MUIDataTable data={courses} columns={columns} options={options} />}
                </Grid>
            </Grid>
        </Box>
    );
};

export default Courses;
